import type {
  ContaReceberApi,
  ContaReceberStatusTipo,
  ContaReceberView,
  ContasReceberApiResponse,
  ContasReceberView,
} from "../types/contas-receber.types";
import {
  diferencaDiasCivisFinanceiro,
  formatarDataCivilFinanceiro,
  formatarDataMovimentacaoFinanceiro,
  hojeYmdFinanceiro,
  ymdCivilFinanceiro,
} from "./formatar-data-financeiro";
import { toNumber } from "./map-dre-api-to-view";

const ORIGEM_LABELS: Record<string, string> = {
  VENDA_LOJA: "Venda loja",
  INSCRICAO_EVENTO: "Inscrição evento",
  PATROCINIO: "Patrocínio",
  AJUSTE_MANUAL: "Ajuste manual",
  ESTORNO_REVERSAO: "Estorno/reversão",
};

const STATUS_LABELS: Record<string, string> = {
  PENDENTE: "Pendente",
  RECEBIDO: "Pago",
  VENCIDO: "Vencido",
  CANCELADO: "Cancelado",
  ESTORNADO: "Estornado",
};

function listaItens(api: ContasReceberApiResponse): ContaReceberApi[] {
  return api.data ?? api.items ?? api.titulos ?? api.registros ?? [];
}

function vencimentoRaw(row: ContaReceberApi): string | null {
  const raw = row.dataVencimento ?? row.vencimentoEm ?? null;
  return raw != null && String(raw).trim() ? String(raw).trim() : null;
}

function statusParaTipo(status: string): ContaReceberStatusTipo {
  const s = status.toUpperCase();
  if (s === "RECEBIDO") return "pago";
  if (s === "VENCIDO") return "vencido";
  if (s === "CANCELADO" || s === "ESTORNADO") return "cancelado";
  if (s === "PENDENTE") return "pendente";
  return "neutro";
}

function calcularVencido(statusRaw: string, vencimentoIso: string | null): boolean {
  if (!vencimentoIso) return false;
  const s = statusRaw.toUpperCase();
  const vencYmd = ymdCivilFinanceiro(vencimentoIso);
  const hojeYmd = hojeYmdFinanceiro();
  if (!vencYmd) return false;
  if (s === "VENCIDO") return true;
  if (s === "PENDENTE" && vencYmd < hojeYmd) return true;
  return false;
}

function calcularDiasAtraso(
  vencido: boolean,
  vencimentoIso: string | null,
): number {
  if (!vencido || !vencimentoIso) return 0;
  const vencYmd = ymdCivilFinanceiro(vencimentoIso);
  const hojeYmd = hojeYmdFinanceiro();
  if (!vencYmd) return 0;
  return Math.max(0, diferencaDiasCivisFinanceiro(vencYmd, hojeYmd));
}

function mapItem(row: ContaReceberApi): ContaReceberView {
  const id = String(row.id ?? "");
  const statusRaw = String(row.status ?? "").trim() || "PENDENTE";
  const vencimentoIso = vencimentoRaw(row);

  const valorBruto = toNumber(row.valorBruto);
  let valorLiquido = toNumber(row.valorLiquido);
  if (valorLiquido === 0 && valorBruto > 0) valorLiquido = valorBruto;

  let valorPago = toNumber(row.valorPago);
  let valorAberto = toNumber(row.valorAberto);

  if (valorAberto === 0 && valorPago === 0 && valorLiquido > 0) {
    const tipo = statusParaTipo(statusRaw);
    if (tipo === "pago") {
      valorPago = valorLiquido;
      valorAberto = 0;
    } else if (tipo === "cancelado") {
      valorPago = 0;
      valorAberto = 0;
    } else {
      valorAberto = valorLiquido;
    }
  }

  if (valorAberto === 0 && valorLiquido > 0 && valorPago > 0) {
    valorAberto = Math.max(0, valorLiquido - valorPago);
  }

  const vencido = calcularVencido(statusRaw, vencimentoIso);
  const diasAtraso = calcularDiasAtraso(vencido, vencimentoIso);

  const ccNome = row.centroCustoNome?.trim();
  const semCentro = !row.centroCustoId || !ccNome;

  const origem = String(row.origem ?? "").trim() || "—";
  const origemLabel = ORIGEM_LABELS[origem] ?? origem.replace(/_/g, " ");

  const clienteNome = row.clienteNome?.trim() || "Cliente não informado";

  return {
    id,
    numero: row.numero != null ? String(row.numero) : "—",
    pedidoId:
      row.pedidoId != null && row.pedidoId !== "" ? String(row.pedidoId) : null,
    pedidoReferencia:
      row.pedidoReferencia?.trim() ? row.pedidoReferencia.trim() : null,
    clienteNome,
    clienteEmail: row.clienteEmail?.trim() ? row.clienteEmail.trim() : null,
    centroCustoNome: semCentro ? "Sem centro" : ccNome!,
    semCentro,
    origem,
    origemLabel,
    descricao: row.descricao?.trim() || "—",
    valorBruto,
    valorLiquido,
    valorPago,
    valorAberto,
    dataVencimento: vencimentoIso,
    dataVencimentoLabel: formatarDataCivilFinanceiro(vencimentoIso),
    competenciaEm: row.competenciaEm?.trim() ?? null,
    pagoEm: row.pagoEm?.trim() ?? null,
    pagoEmLabel: formatarDataMovimentacaoFinanceiro(row.pagoEm, {
      comHora: false,
    }),
    status: statusRaw,
    statusLabel: STATUS_LABELS[statusRaw.toUpperCase()] ?? statusRaw,
    statusTipo: statusParaTipo(statusRaw),
    diasAtraso,
    vencido,
  };
}

function resumoSomentePagina(
  itens: ContaReceberView[],
): ContasReceberView["resumo"] {
  const hojeYmd = hojeYmdFinanceiro();

  let totalAberto = 0;
  let totalVencido = 0;
  let totalVenceHoje = 0;
  let totalRecebido = 0;
  let totalProximos30Dias = 0;
  let quantidadeAberto = 0;
  let quantidadeVencido = 0;

  for (const it of itens) {
    if (it.valorAberto > 0) {
      totalAberto += it.valorAberto;
      quantidadeAberto += 1;
    }
    if (it.valorPago > 0 && it.statusTipo === "pago") {
      totalRecebido += it.valorPago;
    }

    if (it.vencido && it.valorAberto > 0) {
      totalVencido += it.valorAberto;
      quantidadeVencido += 1;
    }

    const venYmd = ymdCivilFinanceiro(it.dataVencimento);
    if (venYmd && it.valorAberto > 0) {
      if (venYmd === hojeYmd) totalVenceHoje += it.valorAberto;
      const dias = diferencaDiasCivisFinanceiro(hojeYmd, venYmd);
      if (dias >= 0 && dias <= 30) totalProximos30Dias += it.valorAberto;
    }
  }

  return {
    totalAberto,
    totalVencido,
    totalVenceHoje,
    totalRecebido,
    totalProximos30Dias,
    quantidadeAberto,
    quantidadeVencido,
  };
}

function mergeResumo(
  api: ContasReceberApiResponse,
  itens: ContaReceberView[],
): ContasReceberView["resumo"] {
  const r = api.resumo;
  if (r && typeof r === "object") {
    return {
      totalAberto: toNumber(r.totalAberto),
      totalVencido: toNumber(r.totalVencido),
      totalVenceHoje: toNumber(r.totalVenceHoje),
      totalRecebido: toNumber(r.totalRecebido),
      totalProximos30Dias: toNumber(r.totalProximos30Dias),
      quantidadeAberto: Number(r.quantidadeAberto ?? 0),
      quantidadeVencido: Number(r.quantidadeVencido ?? 0),
    };
  }
  return resumoSomentePagina(itens);
}

export function mapContasReceberApiToView(
  api: ContasReceberApiResponse | null | undefined,
): ContasReceberView | null {
  if (!api) return null;

  const rawList = listaItens(api);
  const itens = rawList.map(mapItem);

  const total = typeof api.total === "number" ? api.total : itens.length;
  const page = typeof api.page === "number" ? api.page : 1;
  const limit = typeof api.limit === "number" ? api.limit : itens.length || 20;
  const totalPages =
    typeof api.totalPages === "number"
      ? api.totalPages
      : Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return {
    itens,
    total,
    page,
    limit,
    totalPages,
    resumo: mergeResumo(api, itens),
  };
}
